import { alpha } from "@mui/material/styles";
import { colors } from "../../theme/colors";
import {
    ACCOUNT_PAGE_PREMIUM_CTA_SX,
    ACCOUNT_PAGE_SUMMARY_SX,
} from "./accountPageStyles";

const MONO_FONT = "'JetBrains Mono', ui-monospace, monospace";

export const SUBSCRIPTION_STATUS_BAND_SX = {
    ...ACCOUNT_PAGE_SUMMARY_SX,
    display: "grid",
    gridTemplateColumns: {
        xs: "minmax(0, 1fr)",
        sm: "minmax(0, 1fr) 1px minmax(0, 1.2fr)",
    },
    gap: 0,
    width: "100%",
    mt: "1.75rem",
    mb: "2.25rem",
    px: 0,
    py: 0,
    overflow: "hidden",
    boxSizing: "border-box",
};

export const SUBSCRIPTION_STATUS_ZONE_SX = {
    minWidth: 0,
    px: { xs: "1.1rem", sm: "1.35rem" },
    py: { xs: "1.1rem", sm: "1.25rem" },
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
};

export const SUBSCRIPTION_STATUS_PRIMARY_ZONE_SX = {
    ...SUBSCRIPTION_STATUS_ZONE_SX,
};

export const SUBSCRIPTION_STATUS_DIVIDER_SX = {
    alignSelf: "stretch",
    width: { xs: "auto", sm: "1px" },
    height: { xs: "1px", sm: "auto" },
    mx: { xs: "1rem", sm: 0 },
    background: {
        xs: `linear-gradient(90deg, transparent, ${alpha(colors.primary.main, 0.28)} 28%, rgba(255,255,255,0.14) 50%, ${alpha(colors.primary.main, 0.28)} 72%, transparent)`,
        sm: `linear-gradient(180deg, transparent, ${alpha(colors.primary.main, 0.24)} 26%, rgba(255,255,255,0.14) 50%, ${alpha(colors.primary.main, 0.24)} 74%, transparent)`,
    },
    boxShadow: {
        xs: `0 0 12px ${alpha(colors.primary.main, 0.08)}`,
        sm: `0 0 14px ${alpha(colors.primary.main, 0.1)}`,
    },
};

export const SUBSCRIPTION_EYEBROW_SX = {
    color: alpha(colors.text.secondary, 0.66),
    fontSize: "0.64rem",
    fontWeight: 800,
    lineHeight: 1.2,
    letterSpacing: "0.14em",
    textTransform: "uppercase",
};

export const SUBSCRIPTION_PLAN_LINE_SX = {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "0.65rem",
    mt: "0.72rem",
};

export const SUBSCRIPTION_CURRENT_PLAN_SX = {
    display: "inline-block",
    width: "fit-content",
    background: `linear-gradient(180deg, ${colors.accent.soft} 0%, ${colors.primary.main} 100%)`,
    backgroundClip: "text",
    WebkitBackgroundClip: "text",
    color: "transparent",
    WebkitTextFillColor: "transparent",
    fontSize: "1.22rem",
    fontWeight: 780,
    lineHeight: 1.15,
    letterSpacing: "0.01em",
    textShadow: `0 0 20px ${alpha(colors.primary.main, 0.08)}`,
};

export const SUBSCRIPTION_GRANTED_CHIP_SX = {
    display: "inline-flex",
    alignItems: "center",
    minHeight: "1.5rem",
    px: "0.58rem",
    borderRadius: "4px",
    border: `1px solid ${alpha(colors.accent.main, 0.24)}`,
    background: `linear-gradient(180deg, ${alpha(colors.accent.main, 0.1)}, ${alpha(colors.accent.main, 0.045)})`,
    color: colors.accent.main,
    fontSize: "0.62rem",
    fontWeight: 750,
    lineHeight: 1,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
};

export const SUBSCRIPTION_STATUS_DETAIL_SX = {
    mt: "0.48rem",
    color: alpha(colors.text.secondary, 0.9),
    fontSize: "0.81rem",
    fontWeight: 520,
    lineHeight: 1.4,
    fontVariantNumeric: "tabular-nums",
};

export const SUBSCRIPTION_RENEWAL_LABELS_SX = {
    display: "flex",
    alignItems: "baseline",
    justifyContent: "space-between",
    gap: "0.75rem",
    mt: "0.78rem",
    color: alpha(colors.text.secondary, 0.9),
    fontSize: "0.77rem",
    fontWeight: 520,
    lineHeight: 1.35,
};

export const SUBSCRIPTION_RENEWAL_TRACK_SX = {
    display: "grid",
    gridTemplateColumns: "repeat(14, minmax(0, 1fr))",
    gap: "0.2rem",
    width: "100%",
    mt: "0.72rem",
};

export const SUBSCRIPTION_RENEWAL_SEGMENT_SX = {
    height: "0.22rem",
    backgroundColor: "rgba(255,255,255,0.065)",
};

export const SUBSCRIPTION_INLINE_ACTION_SX = {
    alignSelf: "flex-start",
    mt: "0.82rem",
    p: 0,
    border: "none",
    background: "transparent",
    color: colors.primary.main,
    font: "inherit",
    fontSize: "0.67rem",
    fontWeight: 750,
    lineHeight: 1.3,
    letterSpacing: "0.07em",
    textTransform: "uppercase",
    cursor: "pointer",
    transition: "color 0.15s ease, text-shadow 0.15s ease",
    "&:hover": {
        color: colors.accent.soft,
        textShadow: `0 0 14px ${alpha(colors.primary.main, 0.2)}`,
    },
    "&:focus-visible": {
        outline: `1px solid ${colors.primary.main}`,
        outlineOffset: "3px",
    },
};

export const SUBSCRIPTION_SECTION_HEADING_SX = {
    display: "flex",
    alignItems: { xs: "flex-start", sm: "center" },
    justifyContent: "space-between",
    flexDirection: { xs: "column", sm: "row" },
    gap: "0.85rem",
    mb: "1rem",
};

export const SUBSCRIPTION_SECTION_TITLE_SX = {
    m: 0,
    color: colors.text.primary,
    fontSize: { xs: "1.1rem", sm: "1.25rem" },
    fontWeight: 720,
    lineHeight: 1.25,
};

export const SUBSCRIPTION_CYCLE_TOGGLE_SX = {
    position: "relative",
    display: "flex",
    width: { xs: "13.5rem", sm: "14rem" },
    maxWidth: "100%",
    minWidth: 0,
    mx: { xs: "auto", sm: 0 },
    height: "2.5rem",
    p: "3px",
    boxSizing: "border-box",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: "4px",
    background: "linear-gradient(180deg, rgba(255,255,255,0.02), rgba(255,255,255,0.01))",
    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.02)",
};

export const getSubscriptionCycleIndicatorSx = (annual) => ({
    position: "absolute",
    top: "3px",
    left: "3px",
    width: "calc(50% - 3px)",
    height: "calc(100% - 6px)",
    border: `1px solid ${alpha(colors.primary.main, 0.24)}`,
    borderRadius: "2px",
    background: `linear-gradient(180deg, ${alpha(colors.primary.main, 0.16)}, ${alpha(colors.primary.main, 0.07)})`,
    boxShadow: annual ? `0 0 24px ${alpha(colors.primary.main, 0.05)}` : "none",
    transform: annual ? "translateX(100%)" : "translateX(0)",
    transition: "transform 0.35s cubic-bezier(0.22, 1, 0.36, 1), box-shadow 0.25s ease",
    pointerEvents: "none",
});

export const getSubscriptionCycleButtonSx = (active) => ({
    position: "relative",
    zIndex: 1,
    flex: 1,
    minWidth: 0,
    p: 0,
    border: "none",
    borderRadius: 0,
    background: "transparent",
    color: active ? colors.text.primary : colors.text.muted,
    font: "inherit",
    fontSize: "0.65rem",
    fontWeight: 750,
    lineHeight: 1,
    letterSpacing: "0.16em",
    textTransform: "uppercase",
    cursor: "pointer",
    userSelect: "none",
    transition: "color 0.25s ease",
    "&:hover": { color: active ? colors.text.primary : colors.text.secondary },
    "&:focus-visible": {
        outline: `1px solid ${colors.primary.main}`,
        outlineOffset: "-3px",
    },
});

export const SUBSCRIPTION_FOUNDER_NOTICE_SX = {
    display: "flex",
    alignItems: "center",
    width: "fit-content",
    mx: "auto",
    mb: "1rem",
    px: "0.7rem",
    py: "0.45rem",
    border: `1px solid ${alpha(colors.accent.main, 0.2)}`,
    borderRadius: "4px",
    background: `linear-gradient(180deg, ${alpha(colors.accent.main, 0.08)}, ${alpha(colors.accent.main, 0.035)})`,
    color: colors.accent.main,
    fontSize: "0.65rem",
    fontWeight: 800,
    lineHeight: 1,
    letterSpacing: "0.09em",
    textTransform: "uppercase",
};

export const SUBSCRIPTION_FOUNDER_OFFER_SX = {
    position: "relative",
    display: "grid",
    gridTemplateColumns: {
        xs: "minmax(0, 1fr)",
        sm: "minmax(0, 1fr) auto",
    },
    alignItems: "center",
    columnGap: "1.4rem",
    rowGap: "0.75rem",
    width: "100%",
    mb: { xs: "1.65rem", sm: "2rem" },
    pl: { xs: "1rem", sm: "1.2rem" },
    pr: { xs: "0.2rem", sm: "0.3rem" },
    py: { xs: "0.85rem", sm: "0.95rem" },
    boxSizing: "border-box",
    borderTop: `1px solid ${alpha("#fff", 0.045)}`,
    borderBottom: `1px solid ${alpha("#fff", 0.045)}`,
    background: `linear-gradient(90deg, ${alpha(colors.primary.main, 0.045)}, transparent 64%)`,
    "&::before": {
        content: "\"\"",
        position: "absolute",
        top: "0.65rem",
        bottom: "0.65rem",
        left: 0,
        width: "2px",
        background: `linear-gradient(180deg, ${alpha(colors.accent.soft, 0.3)}, ${colors.primary.main}, ${alpha(colors.primary.main, 0.2)})`,
        boxShadow: `0 0 12px ${alpha(colors.primary.main, 0.15)}`,
    },
    "&::after": {
        content: "\"\"",
        position: "absolute",
        top: "50%",
        left: "-0.18rem",
        width: "0.42rem",
        height: "0.42rem",
        border: `1px solid ${alpha(colors.accent.soft, 0.55)}`,
        background: colors.background.base,
        transform: "translateY(-50%) rotate(45deg)",
    },
};

export const SUBSCRIPTION_FOUNDER_COPY_SX = {
    minWidth: 0,
};

export const SUBSCRIPTION_FOUNDER_EYEBROW_SX = {
    display: "flex",
    alignItems: "center",
    gap: "0.42rem",
    color: alpha(colors.accent.main, 0.74),
    fontSize: "0.57rem",
    fontWeight: 780,
    lineHeight: 1.2,
    letterSpacing: "0.11em",
    textTransform: "uppercase",
    "& span": {
        width: "0.28rem",
        height: "0.28rem",
        flexShrink: 0,
        borderRadius: "50%",
        backgroundColor: colors.state.success.main,
        boxShadow: `0 0 7px ${alpha(colors.state.success.main, 0.32)}`,
    },
};

export const SUBSCRIPTION_FOUNDER_DESCRIPTION_SX = {
    mt: "0.28rem",
    color: alpha(colors.text.primary, 0.78),
    fontSize: { xs: "0.7rem", sm: "0.75rem" },
    fontWeight: 520,
    lineHeight: 1.45,
};

export const SUBSCRIPTION_FOUNDER_DISCOUNT_SX = {
    display: "inline-flex",
    alignItems: "center",
    ml: { xs: "0.4rem", sm: "0.55rem" },
    px: "0.45rem",
    py: "0.18rem",
    border: `1px solid ${alpha(colors.accent.main, 0.15)}`,
    borderRadius: "999px",
    background: alpha(colors.primary.main, 0.055),
    color: alpha(colors.accent.soft, 0.88),
    fontSize: "0.57rem",
    fontWeight: 750,
    lineHeight: 1,
    letterSpacing: "0.045em",
    textTransform: "uppercase",
    whiteSpace: "nowrap",
};

export const SUBSCRIPTION_FOUNDER_AVAILABILITY_SX = {
    position: "relative",
    display: "flex",
    alignItems: "baseline",
    justifySelf: { xs: "start", sm: "end" },
    gap: "0.38rem",
    minWidth: { sm: "9.8rem" },
    px: "0.85rem",
    py: "0.62rem",
    overflow: "hidden",
    border: `1px solid ${alpha("#fff", 0.065)}`,
    borderRadius: "7px",
    background: `linear-gradient(145deg, ${alpha("#fff", 0.035)}, ${alpha("#fff", 0.012)})`,
    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.025), 0 8px 20px rgba(0,0,0,0.14)",
    whiteSpace: "nowrap",
    "&::before": {
        content: "\"\"",
        position: "absolute",
        top: 0,
        left: "18%",
        right: "18%",
        height: "1px",
        background: `linear-gradient(90deg, transparent, ${alpha(colors.accent.main, 0.35)}, transparent)`,
    },
};

export const SUBSCRIPTION_FOUNDER_COUNT_SX = {
    color: alpha(colors.text.primary, 0.94),
    fontFamily: MONO_FONT,
    fontSize: "0.98rem",
    fontWeight: 720,
    lineHeight: 1,
    fontVariantNumeric: "tabular-nums",
};

export const SUBSCRIPTION_FOUNDER_COUNT_LABEL_SX = {
    color: colors.text.muted,
    fontSize: "0.55rem",
    fontWeight: 650,
    lineHeight: 1.3,
    letterSpacing: "0.035em",
    textTransform: "uppercase",
};

export const SUBSCRIPTION_PLANS_GRID_SX = {
    display: "grid",
    gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" },
    gap: "1rem",
    alignItems: "stretch",
};

export const SUBSCRIPTION_PLAN_CARD_SX = {
    position: "relative",
    overflow: "hidden",
    minWidth: 0,
    minHeight: "22.5rem",
    p: { xs: "1.75rem", md: "2.15rem" },
    display: "flex",
    flexDirection: "column",
    borderRadius: "14px",
    background: `
        radial-gradient(
            ellipse 75% 55% at 50% -5%,
            rgba(255,255,255,0.045) 0%,
            transparent 68%
        ),
        linear-gradient(
            145deg,
            rgba(18,20,23,0.98) 0%,
            rgba(11,12,14,0.99) 52%,
            rgba(7,8,10,1) 100%
        )
    `,
    border: "1px solid rgba(255,255,255,0.065)",
    boxShadow: `
        inset 0 1px 0 rgba(255,255,255,0.035),
        inset 0 -32px 60px rgba(0,0,0,0.28),
        0 22px 55px rgba(0,0,0,0.22)
    `,
    transition: `
        transform 240ms cubic-bezier(.22,1,.36,1),
        border-color 240ms ease,
        box-shadow 280ms ease
    `,
    "&:hover": {
        transform: "translateY(-3px)",
    },
};

export const SUBSCRIPTION_PREMIUM_CARD_SX = {
    borderColor: "rgba(160,175,195,0.14)",
    "&::before": {
        content: "\"\"",
        position: "absolute",
        top: 0,
        left: "12%",
        right: "12%",
        height: "1px",
        background: `
            linear-gradient(
                90deg,
                transparent,
                rgba(175,190,210,0.48),
                transparent
            )
        `,
    },
    "&:hover": {
        transform: "translateY(-3px)",
        borderColor: "rgba(175,190,210,0.24)",
        boxShadow: `
            inset 0 1px 0 rgba(255,255,255,0.045),
            inset 0 -32px 60px rgba(0,0,0,0.3),
            0 24px 60px rgba(0,0,0,0.3)
        `,
    },
};

export const SUBSCRIPTION_PRO_CARD_SX = {
    borderColor: "rgba(232,163,61,0.26)",
    background: `
        radial-gradient(
            ellipse 60% 45% at 82% 0%,
            rgba(232,163,61,0.075) 0%,
            transparent 72%
        ),
        linear-gradient(
            145deg,
            rgba(19,18,15,0.99) 0%,
            rgba(11,11,11,0.99) 48%,
            rgba(7,8,9,1) 100%
        )
    `,
    boxShadow: `
        inset 0 1px 0 rgba(255,205,125,0.045),
        inset 0 -32px 60px rgba(0,0,0,0.32),
        0 22px 60px rgba(0,0,0,0.28),
        0 0 38px rgba(232,163,61,0.025)
    `,
    "&::before": {
        content: "\"\"",
        position: "absolute",
        top: 0,
        left: "10%",
        right: "10%",
        height: "1px",
        background: `
            linear-gradient(
                90deg,
                transparent,
                rgba(232,163,61,0.38),
                #f3bf68,
                rgba(232,163,61,0.38),
                transparent
            )
        `,
    },
    "&:hover": {
        transform: "translateY(-3px)",
        borderColor: "rgba(232,163,61,0.4)",
        boxShadow: `
            inset 0 1px 0 rgba(255,220,150,0.055),
            inset 0 -32px 60px rgba(0,0,0,0.34),
            0 26px 64px rgba(0,0,0,0.32),
            0 0 44px rgba(232,163,61,0.045)
        `,
    },
};

export const SUBSCRIPTION_PLAN_NAME_ROW_SX = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "1rem",
    minHeight: "3.25rem",
    pb: "1.2rem",
    borderBottom: `1px solid ${colors.border.subtle}`,
};

export const SUBSCRIPTION_PLAN_NAME_SX = {
    color: colors.text.primary,
    fontSize: { xs: "1.35rem", sm: "1.5rem" },
    fontWeight: 760,
    lineHeight: 1.15,
    letterSpacing: "0.01em",
};

export const SUBSCRIPTION_PRICE_BLOCK_SX = {
    mt: "auto",
    pt: { xs: "2rem", sm: "2.35rem" },
};

export const SUBSCRIPTION_PRICE_ROW_SX = {
    display: "flex",
    alignItems: "baseline",
    flexWrap: "wrap",
    columnGap: "0.45rem",
    rowGap: "0.65rem",
};

export const SUBSCRIPTION_PRICE_SX = {
    color: colors.text.primary,
    fontSize: { xs: "2.15rem", sm: "2.45rem" },
    fontWeight: 780,
    lineHeight: 1,
};

export const SUBSCRIPTION_PRICE_CADENCE_SX = {
    color: colors.text.muted,
    fontSize: "0.72rem",
    fontWeight: 550,
};

export const SUBSCRIPTION_PRICE_COMPARE_SX = {
    ml: "auto",
    color: colors.text.muted,
    fontSize: "0.73rem",
    fontWeight: 600,
    textDecoration: "line-through",
    textDecorationThickness: "1px",
};

export const SUBSCRIPTION_BILLING_META_SX = {
    minHeight: "2rem",
    mt: "0.65rem",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "0.75rem",
};

export const SUBSCRIPTION_PRICE_NOTE_SX = {
    color: colors.text.muted,
    fontSize: "0.68rem",
    fontWeight: 500,
    lineHeight: 1.4,
};

export const SUBSCRIPTION_DISCOUNT_SX = {
    flexShrink: 0,
    color: colors.state.success.main,
    fontSize: "0.62rem",
    fontWeight: 800,
    lineHeight: 1,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
};

export const SUBSCRIPTION_PRIMARY_BUTTON_SX = {
    ...ACCOUNT_PAGE_PREMIUM_CTA_SX,
    width: "100%",
    minHeight: "2.8rem",
    mt: "1.25rem",
    px: "1rem",
    py: 0,
    fontFamily: "inherit",
    fontSize: "0.72rem",
    fontWeight: 800,
    lineHeight: 1.2,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    "&:focus-visible": {
        outline: `2px solid ${colors.accent.soft}`,
        outlineOffset: "2px",
    },
};

export const SUBSCRIPTION_COMPARE_ROW_SX = {
    display: "flex",
    justifyContent: "center",
    pt: "1.15rem",
};

export const SUBSCRIPTION_COMPARE_LINK_SX = {
    display: "inline-flex",
    alignItems: "center",
    gap: "0.4rem",
    color: colors.text.muted,
    fontSize: "0.7rem",
    fontWeight: 600,
    lineHeight: 1.3,
    textDecoration: "none",
    "&:hover": { color: colors.accent.main },
    "& svg": { fontSize: "0.86rem" },
};

export const SUBSCRIPTION_REFERRAL_SX = {
    mt: "2.25rem",
    pt: "1.5rem",
    borderTop: `1px solid ${colors.border.subtle}`,
};

export const SUBSCRIPTION_REFERRAL_HEADER_SX = {
    display: "flex",
    alignItems: { xs: "flex-start", sm: "center" },
    justifyContent: "space-between",
    flexDirection: { xs: "column", sm: "row" },
    gap: "0.9rem",
    mb: "1rem",
};

export const SUBSCRIPTION_REFERRAL_TITLE_SX = {
    display: "flex",
    alignItems: "center",
    gap: "0.55rem",
    color: colors.text.primary,
    fontSize: "0.92rem",
    fontWeight: 720,
    lineHeight: 1.3,
    "& svg": { color: colors.accent.main, fontSize: "1.05rem" },
};

export const SUBSCRIPTION_REFERRAL_DESCRIPTION_SX = {
    mt: "0.25rem",
    color: colors.text.secondary,
    fontSize: "0.73rem",
    fontWeight: 500,
    lineHeight: 1.45,
};

export const SUBSCRIPTION_REFERRAL_LINK_SX = {
    display: "inline-flex",
    alignItems: "center",
    gap: "0.35rem",
    p: 0,
    border: "none",
    background: "transparent",
    color: colors.primary.main,
    font: "inherit",
    fontSize: "0.68rem",
    fontWeight: 750,
    lineHeight: 1.3,
    letterSpacing: "0.05em",
    textTransform: "uppercase",
    cursor: "pointer",
    whiteSpace: "nowrap",
    "&:hover": { color: colors.accent.soft },
    "&:focus-visible": {
        outline: `1px solid ${colors.primary.main}`,
        outlineOffset: "3px",
    },
    "& svg": { fontSize: "0.9rem" },
};

export const SUBSCRIPTION_REFERRAL_FIELDS_SX = {
    display: "grid",
    gridTemplateColumns: { xs: "1fr", sm: "minmax(0, 1fr) minmax(0, 1.55fr)" },
    gap: "0.75rem",
};

export const SUBSCRIPTION_REFERRAL_FIELD_SX = {
    ...ACCOUNT_PAGE_SUMMARY_SX,
    minWidth: 0,
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "0.7rem",
    px: "0.95rem",
    py: "0.85rem",
    borderRadius: "12px",
    boxSizing: "border-box",
};

export const SUBSCRIPTION_REFERRAL_VALUE_SX = {
    minWidth: 0,
    overflow: "hidden",
    color: colors.text.primary,
    fontFamily: MONO_FONT,
    fontSize: "0.72rem",
    fontWeight: 650,
    lineHeight: 1.35,
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
};

export const SUBSCRIPTION_COPY_BUTTON_SX = {
    flexShrink: 0,
    width: "2rem",
    height: "2rem",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    p: 0,
    border: `1px solid ${alpha(colors.primary.main, 0.2)}`,
    borderRadius: "5px",
    background: "transparent",
    color: colors.primary.main,
    cursor: "pointer",
    "&:hover": { backgroundColor: alpha(colors.primary.main, 0.08) },
    "&:focus-visible": {
        outline: `1px solid ${colors.primary.main}`,
        outlineOffset: "2px",
    },
    "&:disabled": { opacity: 0.4, cursor: "default" },
    "& svg": { fontSize: "1rem" },
};

export const SUBSCRIPTION_DIALOG_PAPER_SX = {
    borderRadius: "8px",
    border: `1px solid ${colors.border.default}`,
    background: `linear-gradient(165deg, ${colors.background.card}, ${colors.background.dark})`,
    backgroundImage: "none",
    color: colors.text.primary,
    boxShadow: "0 28px 70px rgba(0,0,0,0.6)",
};

export const SUBSCRIPTION_DIALOG_DETAIL_ROW_SX = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "1rem",
    py: "0.65rem",
    borderBottom: `1px solid ${colors.border.subtle}`,
    color: colors.text.secondary,
    fontSize: "0.78rem",
    "&:last-of-type": { borderBottom: "none" },
};
